import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Plus, Percent, RefreshCw } from "lucide-react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { toast } from "sonner";
import { commissionRateService } from "@/features/commission-rates/services/commission-rate.service";
import type {
  CommissionRateResponseDTO,
  CommissionRateFormData,
} from "@/features/commission-rates/types/commission-rate.types";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import {
  dateLocalToIso,
  dateLocalToIsoEndOfDay,
} from "@/shared/lib/date-format";
import { applyFieldErrorsToForm } from "@/shared/lib/form-error";
import { CommissionRatesTable } from "@/features/commission-rates/components/CommissionRatesTable";
import {
  CommissionRateFormDialog,
  RATE_TYPE_LABELS,
} from "@/features/commission-rates/components/CommissionRateFormDialog";
import { CommissionRateDeleteDialog } from "@/features/commission-rates/components/CommissionRateDeleteDialog";

const DIALOG_MODE = {
  CREATE: "create",
  EDIT: "edit",
  DELETE: "delete",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

const commissionRateSchema = z
  .object({
    name: z.string().min(1, "Nome é obrigatório"),
    type: z.enum(["SUIT", "SHOES", "GOWN", "GRADUATION"]),
    chargeType: z.enum(["percentage", "fixed"]),
    rate: z.union([
      z.number().min(0.01, "Percentual deve ser maior que 0").max(100),
      z.literal(""),
    ]),
    value: z.union([
      z.number().min(0.01, "Valor deve ser maior que 0"),
      z.literal(""),
    ]),
    initialDate: z.string().min(1, "Data inicial é obrigatória"),
    finalDate: z.string(),
  })
  .superRefine((data, ctx) => {
    if (
      data.chargeType === "percentage" &&
      (data.rate === "" || data.rate === undefined)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["rate"],
        message: "Percentual é obrigatório",
      });
    }
    if (
      data.chargeType === "fixed" &&
      (data.value === "" || data.value === undefined)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["value"],
        message: "Valor fixo é obrigatório",
      });
    }
  });

export function TaxasComissaoPage() {
  const [commissionRates, setCommissionRates] = useState<
    CommissionRateResponseDTO[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedCommissionRate, setSelectedCommissionRate] =
    useState<CommissionRateResponseDTO | null>(null);

  const form = useForm<CommissionRateFormData>({
    resolver: zodResolver(commissionRateSchema),
    defaultValues: {
      name: "",
      type: "SUIT",
      chargeType: "percentage",
      rate: "",
      value: "",
      initialDate: "",
      finalDate: "",
    },
  });

  const loadCommissionRates = useCallback(
    async (page: number = 0) => {
      setLoading(true);
      try {
        const response = await commissionRateService.listPaginated({
          page,
          size: pageSize,
        });
        setCommissionRates(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        setCurrentPage(page);
      } catch (error: unknown) {
        toast.error(
          getApiErrorMessage(error, "Erro ao carregar taxas de comissão"),
        );
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    // Recarrega só ao trocar pageSize. Padrão legado Família A;
    // migrar para useCommissionRateList em refator futuro.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadCommissionRates(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  const filteredCommissionRates = useMemo(() => {
    if (!searchTerm) return commissionRates;
    const search = searchTerm.toLowerCase();
    return commissionRates.filter((cr) => {
      const chargeLabel = cr.rate !== null ? "percentual" : "fixa";
      const typeLabel = (RATE_TYPE_LABELS[cr.type] ?? cr.type).toLowerCase();
      const name = `comissão ${chargeLabel} ${typeLabel}`;
      const value =
        cr.rate !== null
          ? `${(cr.rate * 100).toFixed(1)}%`
          : (cr.value?.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            }) ?? "");
      const displayName = (cr.name ?? name).toLowerCase();
      return (
        displayName.includes(search) ||
        typeLabel.includes(search) ||
        value.includes(search)
      );
    });
  }, [commissionRates, searchTerm]);

  const handleOpenDialog = useCallback(
    (mode: DialogMode, commissionRate?: CommissionRateResponseDTO) => {
      setDialogMode(mode);
      setDialogContent(mode);
      setSelectedCommissionRate(commissionRate ?? null);

      if (mode === DIALOG_MODE.EDIT && commissionRate) {
        form.reset({
          name: commissionRate.name ?? "",
          type: commissionRate.type,
          chargeType: commissionRate.rate !== null ? "percentage" : "fixed",
          rate: commissionRate.rate !== null ? commissionRate.rate * 100 : "",
          value: commissionRate.value !== null ? commissionRate.value : "",
          initialDate: commissionRate.initialDate?.slice(0, 10) ?? "",
          finalDate: commissionRate.finalDate?.slice(0, 10) ?? "",
        });
      } else if (mode === DIALOG_MODE.CREATE) {
        form.reset({
          name: "",
          type: "SUIT",
          chargeType: "percentage",
          rate: "",
          value: "",
          initialDate: "",
          finalDate: "",
        });
      }
    },
    [form],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    setTimeout(() => {
      setDialogContent(null);
      setSelectedCommissionRate(null);
      form.reset();
    }, 300);
  }, [form]);

  const onSubmit = useCallback(
    async (data: CommissionRateFormData) => {
      setLoading(true);
      try {
        const payload = {
          name: data.name,
          type: data.type,
          rate:
            data.chargeType === "percentage" ? Number(data.rate) / 100 : null,
          value: data.chargeType === "fixed" ? Number(data.value) : null,
          // Interpreta como início/fim do dia no fuso local — sem `Z`
          // acabávamos enviando UTC (dia anterior em BR).
          initialDate: dateLocalToIso(data.initialDate),
          finalDate: data.finalDate
            ? dateLocalToIsoEndOfDay(data.finalDate)
            : null,
        };

        if (dialogMode === DIALOG_MODE.CREATE) {
          const newRate = await commissionRateService.create(payload);
          toast.success("Taxa de comissão criada com sucesso!");
          setCommissionRates((prev) => [newRate, ...prev]);
          setTotalElements((prev) => prev + 1);
        } else if (dialogMode === DIALOG_MODE.EDIT && selectedCommissionRate) {
          const updatedRate = await commissionRateService.update(
            selectedCommissionRate.id,
            payload,
          );
          toast.success("Taxa de comissão atualizada com sucesso!");
          setCommissionRates((prev) =>
            prev.map((cr) => (cr.id === updatedRate.id ? updatedRate : cr)),
          );
        }

        handleCloseDialog();
      } catch (error: unknown) {
        if (!applyFieldErrorsToForm(error, form)) {
          toast.error(
            getApiErrorMessage(error, "Erro ao salvar taxa de comissão"),
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [
      dialogMode,
      selectedCommissionRate,
      handleCloseDialog,
      loadCommissionRates,
      form,
      currentPage,
    ],
  );

  const handleDelete = useCallback(async () => {
    if (!selectedCommissionRate) return;
    setLoading(true);
    try {
      await commissionRateService.remove(selectedCommissionRate.id);
      toast.success("Taxa de comissão excluída com sucesso!");
      setCommissionRates((prev) =>
        prev.filter((cr) => cr.id !== selectedCommissionRate.id),
      );
      setTotalElements((prev) => prev - 1);
      handleCloseDialog();
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(error, "Erro ao excluir taxa de comissão"),
      );
    } finally {
      setLoading(false);
    }
  }, [selectedCommissionRate, handleCloseDialog]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage >= 0 && newPage < totalPages) {
        loadCommissionRates(newPage);
      }
    },
    [totalPages, loadCommissionRates],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Taxa de Comissões
          </h1>
          <p className="text-muted-foreground">
            Gerencie as taxas de comissões
          </p>
        </div>
        <Can permission={ACTIONS.commissionRates.create}>
          <Button
            className="gap-2"
            onClick={() => handleOpenDialog(DIALOG_MODE.CREATE)}
            disabled={loading}
          >
            <Plus className="w-4 h-4" />
            Nova Taxa
          </Button>
        </Can>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Percent className="w-5 h-5 text-muted-foreground" />
              <div>
                <CardTitle>Taxa de Comissões</CardTitle>
                <CardDescription>
                  {loading
                    ? "Carregando..."
                    : `Mostrando ${filteredCommissionRates.length} de ${totalElements} taxas`}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Buscar taxa de comissão..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="md:max-w-xs"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => loadCommissionRates(currentPage)}
                disabled={loading}
                title="Atualizar lista"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
              </Button>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                disabled={loading}
                className="h-10 cursor-pointer rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value={5}>5 / página</option>
                <option value={10}>10 / página</option>
                <option value={25}>25 / página</option>
                <option value={50}>50 / página</option>
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && commissionRates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Carregando taxas de comissão...
            </div>
          ) : filteredCommissionRates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma taxa de comissão encontrada
            </div>
          ) : (
            <CommissionRatesTable
              commissionRates={filteredCommissionRates}
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onEdit={(cr) => handleOpenDialog(DIALOG_MODE.EDIT, cr)}
              onDelete={(cr) => handleOpenDialog(DIALOG_MODE.DELETE, cr)}
              onPageChange={handlePageChange}
            />
          )}
        </CardContent>
      </Card>

      <CommissionRateFormDialog
        open={
          dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
        }
        mode={dialogContent === DIALOG_MODE.CREATE ? "create" : "edit"}
        loading={loading}
        form={form}
        onClose={handleCloseDialog}
        onSubmit={onSubmit}
      />

      <CommissionRateDeleteDialog
        open={dialogMode === DIALOG_MODE.DELETE}
        loading={loading}
        commissionRate={selectedCommissionRate}
        onClose={handleCloseDialog}
        onConfirm={handleDelete}
      />
    </div>
  );
}
